import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import * as XLSX from "xlsx";
import { comnEnvs, comnUtils } from "@/comn/utils";
import { lpcoEnvs, lpcoUtils } from "@/tz/go/egaz/osci/lpco/comn"; // 시스템 공통 유틸
import { Page, Group, Layout, Button, Grid, ExcelUpload, FormControl, Table, Tree } from "@/comn/components";
import {
    useForm,
    useToast,
    useFetch,
    useModal,
    useAuth,
    useStore,
    useGrid,
    usePopup,
    useResource,
    useExcel,
    useTree,
    usePage,
} from "@/comn/hooks";
import {
    BASE,
    URLS,
    APIS,
    SF_LPCO_INFO_R,
    SG_ORG_DEPT_USR_LIST_U,
    SG_SETT_OFCR_LIST_R,
} from "./services/OsciCfgnChpnService";
import { CommonErrors } from "@/comn/components";

/*
 * @ 화면 컴포넌트 주석
 * @ 메타 시스템을 참고 하여 표준 단어와 용어를 사용하여 작성
 * @ !== ... ==! 치환 부호는 개발 완료 후 한글 주석을 인지하여 제거하기 위한 표시
 * @ 기본 여러줄의 주석 형태를 사용
 * @ 포맷
 * @ [화면명 영문]
 * @ [지환부호시작] [화면명 한글] [치환부호종료]
 *
 * 화면명 영문
 * !== 화면명 한글 ==!
 */

/*
 * LPCO Process Details
 * !== LPCO Process 상세 ==!
 */
export const OSCI0203027S = () => {
    const { pgeUid, menuId } = usePage({
        auth: ["auth_osci0004"],
    });
    const { t } = useTranslation(); // Translation Hook !== 언어 변환 Hook ==!
    const navigate = useNavigate(); // Navigate Hook !== 화면 이동 Hook ==!
    const modal = useModal(); // Modal Window Hook !== Modal 창 Hook ==!
    const { pgeStore, setStore, getStore } = useStore({ pgeUid: pgeUid }); // Page Store Hook !== 화면 데이터 저장 Hook ==!
    const toast = useToast(); // Toast Message Hook !== Toast 메세지 표시 Hook ==!
    const auth = useAuth(); // Login , User Information Hook !== 로그인, 유저정보 Hook ==!
    const { appFmId } = useParams(); // Key Information of this Component Router !== 라우터에 정의된 키정보 ==!
    const { openPopup } = usePopup();
    // useTree
    const uTree = useTree({
        _checkbox: false,
        _checked: [],
    });

    const [orgDeptTree, setOrgDeptTree] = useState([]);

    const [orgDeptUsrList, setOrgDeptUsrList] = useState(comnUtils.getGridData([]));
    const [ofcrList, setOfcrList] = useState(comnUtils.getGridData([]));

    // ORG LIST
    const [orgCdList, setOrgCdList] = useState([]);
    // ORG LPCO LIST
    const [orgLpcoList, setOrgLpcoList] = useState([]);

    useResource({
        defaultSchema: [
            { area: "comnCd", comnCd: "OSC0116" },
            { area: "comnCd", comnCd: "OSC0120" },
            { area: "comnCd", comnCd: "OSC0119" },
            { area: "comnCd", comnCd: "OSC0113" },
        ],
    });

    /*
     * Declaration Form
     * !== 폼 정의 ==!
     */
    const form = {
        // LPCO Process Details !== LPCO Process 상세 ==!
        lpcoInfoDtl: useForm({
            defaultSchema: SF_LPCO_INFO_R,
            defaultValues: {
                orgCd: auth.get("orgCd"),
                orgNm: auth.get("orgNm"),
            },
        }),
    };

    /*
     * Declaration Grid
     * !== 그리드 정의 ==!
     */
    const grid = {
        ofcrList: useGrid({
            defaultSchema: SG_SETT_OFCR_LIST_R,
            page: pgeStore?.page,
            size: pgeStore?.size,
        }),
    };

    const render = {
        grid_ofcrList: {
            head: {},
            cell: {},
            edit: {},
        },
    };

    /*
     * Declaration Fetch
     * !== Fetch 정의 ==!
     */
    const fetch = {
        /* Register LPCO Component !== LPCO Component 등록 ==! */
        saveAppChpn: useFetch({
            api: (data) => APIS.saveAppChpn({ ...data }),
            onSuccess: (data) => {
                modal.openModal({
                    content: "msg.osc.00006",
                    onConfirm: () => {
                        navigate(URLS.osci0203028s + "/" + data.lpcoCnpn.content.appFmId);
                    },
                    onCancel: () => {
                        //navigate(URLS.osci0203001s);
                    },
                });
            },
            onError: (error) => {
                console.log(error);
                if (error.response.status === 400) {
                    console.log(error.response.data);
                    form.lpcoInfoDtl.setErrors(error.response.data.errors);
                }

                const errMsg = error?.response?.data?.errorMessage || "msg.osc.00015";
                modal.openModal({
                    content: errMsg,
                });
            },
        }),
        getOrgLpcoByPrcsList: useFetch({
            api: (data) => {
                return lpcoUtils.getOrgLpcoByPrcsList(data);
            },
            onSuccess: (data) => {
                console.log("getOrgLpcoByPrcsList onSuccess ", data);
                setOrgLpcoList(data || []);
            },
            onError: () => {},
            showToast: false,
        }),
        getOrgDeptTreeData: useFetch({
            api: (data) => {
                return lpcoUtils.getOrgDeptTreeData(data);
            },
            onSuccess: (data) => {
                console.log("getOrgDeptTreeData onSuccess ", data);
                setOrgDeptTree(data);
                uTree.setData(data);
            },
            onError: () => {},
            showToast: false,
        }),
        getAppDtl: useFetch({
            api: (data) => {
                return APIS.getAppDtl(data);
            },
            onSuccess: (data) => {
                console.log("getAppDtl onSuccess ==================== ", data);
                form.lpcoInfoDtl.setValues(data?.appInfo?.content?.info || {});

                const chpnList = data?.appInfo?.content?.chpnList || [];
                setOfcrList(comnUtils.getGridData(chpnList));
            },
            onError: () => {},
            showToast: false,
        }),
    };

    /*
     * 화면에서 사용하는 이벤트, 함수를 선언
     * 기본 변수명은 메타 단어 조합 카멜 표기법을 따름
     * fetch 를 호출 하는 메소드: fetch 명과 동일하게 작성
     * 버튼이나 그리드의 이벤트의 경우 : [이벤트종류]_[이벤트대상타입]_[이벤트대상명]
     * 이벤트 대상 타입 : Btn , Grid ... 카멜표기법으로 표시, 첫글자 대문자
     * 이벤트 명 : 카멜표기법으로 표시, 첫글자 대문자
     *
     * const handler = {
     *      [handler 이름] : ()=>{ 함수 },
     *      click_Btn_Save : 저장버튼 클릭
     *      click_Grid_RpckItmAppList : 재포장 품목 신청서 그리드 클릭
     *      ...
     * }
     */
    /*
     * Declaration Event Handler
     * !== 이벤트 핸들러 정의 ==!
     */

    const handler = {
        setInit: async () => {
            handler.setAppDtl(appFmId);
        },
        setAppDtl: (appFmId: any) => {
            fetch.getAppDtl.fetch(appFmId);
        },
    };

    useEffect(() => {
        handler.setInit();
        uTree.tree._tree.current._checkbox = false;
    }, []);

    return (
        <Page
        // id={pgeUid}
        // title={t("T_DEPT_OFCR_CRGE_DTL")}
        // navigation={{
        //     base: comnEnvs.base,
        //     nodes: [
        //         ...BASE.nodes,
        //         { path: "/eswo/lpco/cfgn/chpn/osci0203026q", label: "T_DEPT_OFCR_CRGE_LST" },
        //         { path: "/eswo/lpco/cfgn/chpn/osci0203027s", label: "T_DEPT_OFCR_CRGE_DTL" },
        //     ],
        // }}
        >
            <Group>
                <Group.Body>
                    <Group.Title title={"T_LPCO_APP_INFO"} titleSize={2}></Group.Title>
                    <Group.Section>
                        <Group.Row>
                            <Group.Control {...form.lpcoInfoDtl.schema.orgNm}></Group.Control>
                        </Group.Row>
                        <Group.Row>
                            <Group.Control {...form.lpcoInfoDtl.schema.appNm}></Group.Control>
                            <Group.Control {...form.lpcoInfoDtl.schema.appTpCd}></Group.Control>
                        </Group.Row>
                    </Group.Section>
                </Group.Body>
            </Group>

            <Group>
                <Group.Body>
                    <Group.Title title={"T_OFCR"} titleSize={2}></Group.Title>
                    <Layout direction="row">
                        <Layout.Left></Layout.Left>
                        <Layout.Right></Layout.Right>
                    </Layout>
                    {/*
                     * 그리드
                     * @ 그리드 스키마 주입 : {...grid.[그리드이름].grid}
                     * @ 데이터 data={fetch.[fetch 명].data?.[api 리턴 vo 명]}
                     * @ 셀클릭이벤트 연결 : onCellClick={handler.[그리드 이벤트 핸들러명]}
                     */}
                    <Grid
                        {...grid.ofcrList.grid}
                        data={ofcrList} /* fetch.getRpckItmAppList.data?.orgDeptUsrList */
                        render={render.grid_ofcrList}
                    />
                </Group.Body>
            </Group>

            <Layout direction="row">
                <Layout.Left>
                    <Button
                        role="list"
                        onClick={() => {
                            navigate(URLS.osci0203026q);
                        }}
                    ></Button>
                </Layout.Left>
                <Layout.Right>
                    {/* <Button role="edit" onClick={handler.click_Btn_AddGridLpcoAtcdFileList}></Button> */}
                    <Button
                        role="edit"
                        onClick={() => {
                            navigate(`${URLS.osci0203028s}/${appFmId}`);
                        }}
                    ></Button>
                    {/* <Button
                        role="delete"
                        onClick={() => {
                            alert("삭제 미개발");
                        }}
                    ></Button> */}
                </Layout.Right>
            </Layout>
        </Page>
    );
};
